import { 
    NotificationEntityType, 
    RecipientType, 
    ReminderCategory, 
    ReminderRecipientGroupCodes, 
    ReminderTriggerPeriod 
} from "@/models/enum"

// Type definitions for the mapping
export interface ReminderFieldMapping {
  RecipientType: string
  ReminderCategory: string
  RecipientCodes: string[]
  TriggerTypes: string[]
}

// The flattened mapping data - using enum values
const REMINDER_FIELD_MAPPINGS: ReminderFieldMapping[] = [
  {
    RecipientType: RecipientType.WORKER,
    ReminderCategory: ReminderCategory.TimesheetSubmissionOverdueReminder,
    RecipientCodes: [
      ReminderRecipientGroupCodes.OnlineTimesheetWorkers, 
      ReminderRecipientGroupCodes.OfflineTimesheetWorks, // Note: enum has "Works" not "Workers"
      ReminderRecipientGroupCodes.VmsTimesheetWorkers
    ],
    TriggerTypes: [ReminderTriggerPeriod.BeforePeriodEnds, ReminderTriggerPeriod.AfterPeriodEnds]
  },
  {
    RecipientType: RecipientType.CLIENT,
    ReminderCategory: ReminderCategory.TimesheetApprovalOverdueReminder,
    RecipientCodes: [ReminderRecipientGroupCodes.OnlineTimesheetApprovers],
    TriggerTypes: [ReminderTriggerPeriod.AfterSubmission]
  },
  {
    RecipientType: RecipientType.CLIENT,
    ReminderCategory: ReminderCategory.ExpenseApprovalOverdueReminder,
    RecipientCodes: [ReminderRecipientGroupCodes.OnlineExpenseApprovers],
    TriggerTypes: [ReminderTriggerPeriod.AfterSubmission]
  },
  {
    RecipientType: RecipientType.CLIENT,
    ReminderCategory: ReminderCategory.TimesheetAndExpenseApprovalOverdueReminder,
    RecipientCodes: [
      ReminderRecipientGroupCodes.OnlineTimesheetApprovers, 
      ReminderRecipientGroupCodes.OnlineExpenseApprovers
    ],
    TriggerTypes: [ReminderTriggerPeriod.AfterSubmission]
  },
  {
    RecipientType: RecipientType.INTERNAL,
    ReminderCategory: ReminderCategory.TimesheetSubmissionOverdueReminder,
    RecipientCodes: ["WorkOrderAssignedTo", "WorkOrderJobOwner", "WorkOrderRecruiter"], // Custom values for internal users
    TriggerTypes: [ReminderTriggerPeriod.AfterPeriodEnds]
  },
  {
    RecipientType: RecipientType.INTERNAL,
    ReminderCategory: ReminderCategory.TimesheetApprovalOverdueReminder,
    RecipientCodes: ["WorkOrderAssignedTo", "WorkOrderJobOwner", "WorkOrderRecruiter"],
    TriggerTypes: [ReminderTriggerPeriod.AfterSubmission]
  },
  {
    RecipientType: RecipientType.INTERNAL,
    ReminderCategory: ReminderCategory.ExpenseApprovalOverdueReminder,
    RecipientCodes: ["WorkOrderAssignedTo", "WorkOrderJobOwner", "WorkOrderRecruiter"],
    TriggerTypes: [ReminderTriggerPeriod.AfterSubmission]
  },
  {
    RecipientType: RecipientType.INTERNAL,
    ReminderCategory: ReminderCategory.TimesheetAndExpenseApprovalOverdueReminder,
    RecipientCodes: ["WorkOrderAssignedTo", "WorkOrderJobOwner", "WorkOrderRecruiter"],
    TriggerTypes: [ReminderTriggerPeriod.AfterPeriodEnds, ReminderTriggerPeriod.AfterSubmission]
  }
]

// Helper function to normalize entity types (expand TIMESHEET_EXPENSE)
export function normalizeEntityTypes(entityTypes: string[]): string[] {
  const expandedEntityTypes: string[] = []
  
  entityTypes.forEach((entityType: string) => {
    if (entityType === NotificationEntityType.TIMESHEET_EXPENSE) {
      expandedEntityTypes.push(NotificationEntityType.TIMESHEET)
      expandedEntityTypes.push(NotificationEntityType.EXPENSE_CLAIM)
    } else {
      expandedEntityTypes.push(entityType)
    }
  })
  
  return expandedEntityTypes.sort() // Sort for consistent comparison
}

// Helper function to determine reminder category based on recipient type and entity types
export function getReminderCategoryFromEntityTypes(recipientType: string, entityTypes: string[], triggerType: string): string {
    const normalizedEntityTypes = normalizeEntityTypes(entityTypes)

    if (recipientType === RecipientType.WORKER) {
        return ReminderCategory.TimesheetSubmissionOverdueReminder
    }

    if (recipientType === RecipientType.CLIENT || recipientType === RecipientType.INTERNAL) {
        const hasTimesheet = normalizedEntityTypes.includes(NotificationEntityType.TIMESHEET)
        const hasExpense = normalizedEntityTypes.includes(NotificationEntityType.EXPENSE_CLAIM)

        if (hasTimesheet && hasExpense) {
            if(recipientType === RecipientType.INTERNAL && triggerType === ReminderTriggerPeriod.AfterPeriodEnds){
                return ReminderCategory.TimesheetSubmissionOverdueReminder
            }
            return ReminderCategory.TimesheetAndExpenseApprovalOverdueReminder
        } else if (hasTimesheet) {
            if(recipientType === RecipientType.CLIENT){
                return ReminderCategory.TimesheetApprovalOverdueReminder
            }else{
                // For Internal
                if(triggerType === ReminderTriggerPeriod.AfterPeriodEnds){
                    return ReminderCategory.TimesheetSubmissionOverdueReminder
                }else{
                    return ReminderCategory.TimesheetApprovalOverdueReminder
                }
            }
        } else if (hasExpense) {
            return ReminderCategory.ExpenseApprovalOverdueReminder
        }
    }

    return ""
}

// Main function to get field mapping based on recipient type and entity types
export function getReminderFieldMapping(recipientType: string, entityTypes: string[], triggerType: string): ReminderFieldMapping | null {
    const reminderCategory = getReminderCategoryFromEntityTypes(recipientType, entityTypes, triggerType)

    // Find the mapping that matches both recipient type and reminder category
    const mapping = REMINDER_FIELD_MAPPINGS.find(
        m => m.RecipientType === recipientType && m.ReminderCategory === reminderCategory
    )

    return mapping || null
}

// Helper function to get available recipient codes for a given configuration
export function getAvailableRecipientCodes(recipientType: string, entityTypes: string[], triggerType: string): string[] {
    const mapping = getReminderFieldMapping(recipientType, entityTypes, triggerType)
    return mapping?.RecipientCodes || []
}

// Helper function to get available trigger types for a given configuration
export function getAvailableTriggerTypes(recipientType: string, entityTypes: string[], triggerType: string): string[] {
    const mapping = getReminderFieldMapping(recipientType, entityTypes, triggerType)
    return mapping?.TriggerTypes || []
}

// Helper function to get default values for form initialization
export function getDefaultReminderValues(recipientType: string, entityTypes: string[], triggerType: string) {
  const mapping = getReminderFieldMapping(recipientType, entityTypes, triggerType)
  
  if (!mapping) {
    return {
      reminderCategory: "",
      recipientCodes: [],
      triggerType: "",
      defaultTriggerType: "",
      internalRecipientCheckboxes: {},
      internalAdditionalEmails: []
    }
  }
  
  const result = {
    reminderCategory: mapping.ReminderCategory,
    recipientCodes: mapping.RecipientCodes,
    triggerType: mapping.TriggerTypes[0], // Default to first available trigger type
    defaultTriggerType: mapping.TriggerTypes[0],
    internalRecipientCheckboxes: {},
    internalAdditionalEmails: []
  }
  
  // For Internal recipients, set up checkbox defaults
  if (recipientType === RecipientType.INTERNAL) {
    const checkboxDefaults: { [key: string]: boolean } = {}
    mapping.RecipientCodes.forEach(code => {
      checkboxDefaults[code] = false // Default to not checked
    })
    result.internalRecipientCheckboxes = checkboxDefaults
  }
  
  return result
}

// Helper function to validate if a recipient code is valid for the given configuration
export function isValidRecipientCode(recipientType: string, entityTypes: string[], triggerType: string, recipientCode: string): boolean {
    const availableCodes = getAvailableRecipientCodes(recipientType, entityTypes, triggerType)
    return availableCodes.includes(recipientCode)
}

// Helper function to validate if a trigger type is valid for the given configuration
export function isValidTriggerType(recipientType: string, entityTypes: string[], triggerType: string): boolean {
    const availableTriggerTypes = getAvailableTriggerTypes(recipientType, entityTypes, triggerType)
    return availableTriggerTypes.includes(triggerType)
}

// Helper function to expand "AllWorkers" into individual worker types (for Workers category)
export function expandWorkerRecipientCodes(recipientCodes: string[]): string[] {
  const expandedCodes: string[] = []
  
  recipientCodes.forEach(code => {
    if (code === ReminderRecipientGroupCodes.AllWorkers) {
      // Expand AllWorkers to include all individual worker types
      expandedCodes.push(
        ReminderRecipientGroupCodes.OnlineTimesheetWorkers,
        ReminderRecipientGroupCodes.OfflineTimesheetWorks,
        ReminderRecipientGroupCodes.VmsTimesheetWorkers
      )
    } else {
      expandedCodes.push(code)
    }
  })
  
  return expandedCodes
}

// Helper function to get the final recipient codes for API submission
export function getFinalRecipientCodes(recipientType: string, entityTypes: string[], selectedRecipientCodes: string[], additionalEmails?: string[]): string[] {
  if (recipientType === RecipientType.WORKER) {
    return expandWorkerRecipientCodes(selectedRecipientCodes)
  }
  
  if (recipientType === RecipientType.CLIENT) {
    // For clients, determine recipient codes based on entity types
    const normalizedEntityTypes = normalizeEntityTypes(entityTypes)
    const codes: string[] = []
    
    if (normalizedEntityTypes.includes(NotificationEntityType.TIMESHEET)) {
      codes.push(ReminderRecipientGroupCodes.OnlineTimesheetApprovers)
    }
    if (normalizedEntityTypes.includes(NotificationEntityType.EXPENSE_CLAIM)) {
      codes.push(ReminderRecipientGroupCodes.OnlineExpenseApprovers)
    }
    
    return codes
  }
  
  if (recipientType === RecipientType.INTERNAL) {
    // For Internal, combine selected checkbox codes with additional emails if provided
    const codes = [...selectedRecipientCodes]
    if (additionalEmails && additionalEmails.length > 0) {
      // Filter out empty emails and add valid ones
      const validEmails = additionalEmails.filter(email => email && email.trim())
      codes.push(...validEmails)
    }
    return codes
  }
  
  return selectedRecipientCodes
}

// Helper function to get available options for the "What" dropdown (entity types)
export function getAvailableEntityTypeOptions(recipientType: string): { value: string; label: string }[] {
  if (recipientType === RecipientType.WORKER) {
    // Workers only deal with timesheets
    return [
      { value: NotificationEntityType.TIMESHEET, label: 'Timesheets' }
    ]
  }
  
  if (recipientType === RecipientType.CLIENT || recipientType === RecipientType.INTERNAL) {
    return [
      { value: NotificationEntityType.TIMESHEET, label: 'Timesheets' },
      { value: NotificationEntityType.EXPENSE_CLAIM, label: 'Expenses' },
      { value: NotificationEntityType.TIMESHEET_EXPENSE, label: 'Timesheets and Expenses' }
    ]
  }
  
  return []
}

// Helper function to get available options for the "Who" dropdown (recipient codes)
export function getAvailableRecipientOptions(recipientType: string, entityTypes: string[]): { value: string; label: string }[] {
  if (recipientType === RecipientType.WORKER) {
    return [
      { value: ReminderRecipientGroupCodes.AllWorkers, label: 'All Workers' },
      { value: ReminderRecipientGroupCodes.OnlineTimesheetWorkers, label: 'Workers with Online Timesheets' },
      { value: ReminderRecipientGroupCodes.OfflineTimesheetWorks, label: 'Workers with Offline Timesheets' },
      { value: ReminderRecipientGroupCodes.VmsTimesheetWorkers, label: 'Workers with VMS Timesheets' }
    ]
  }
  
  // For clients, recipient codes are determined automatically based on entity types
  // For internal, recipient codes are now selected via checkboxes (handled separately)
  return []
}

// Helper function to get available Internal recipient checkbox options
export function getInternalRecipientCheckboxOptions(): { value: string; label: string }[] {
  return [
    { value: "WorkOrderAssignedTo", label: "Assigned To" },
    { value: "WorkOrderJobOwner", label: "Job Owner" },
    { value: "WorkOrderRecruiter", label: "Recruiter" }
  ]
}

export function getDefaultTriggerType(recipientType: string): string {
    if (recipientType === RecipientType.WORKER) {
        return ReminderTriggerPeriod.BeforePeriodEnds
    }
    if(recipientType === RecipientType.CLIENT) {
        return ReminderTriggerPeriod.AfterSubmission
    }
    if(recipientType === RecipientType.INTERNAL) {
        return ReminderTriggerPeriod.AfterPeriodEnds
    }

    return ''
}